import { css } from '@emotion/react';
import { useCallback } from 'react';
import { UseFieldArrayRemove, UseFieldArrayReplace, useFormContext } from 'react-hook-form';
import { IItemWithId, SortableList } from '../../../../components/SortableList/SortableList';
import {
  CustomKpiTemplateFormValues,
  CustomTemplateSectionType,
  TemplateSectionFormValues,
} from '../../../../schemas/kpi-template.schema';
import { TemplateSectionItem } from './TemplateSectionItem';

const sectionListCss = css`
  & > ul {
    row-gap: 0.75rem;
  }
`;

type SectionField = TemplateSectionFormValues & { id: string };
type SortableSectionField = SectionField & IItemWithId;

interface TemplateSectionListProps {
  fields: SectionField[];
  remove: UseFieldArrayRemove;
  replace: UseFieldArrayReplace<CustomKpiTemplateFormValues, 'sections'>;
  onCreateNewKpi?: (sectionIndex: number, sectionType: CustomTemplateSectionType, kpiName?: string) => void;
}

function useTemplateSectionList(replace: TemplateSectionListProps['replace']) {
  const { watch } = useFormContext<CustomKpiTemplateFormValues>();

  const handleReorder = useCallback(
    (reordered: SortableSectionField[]) => {
      // Read from the form store via watch — never pass `fields` objects to replace,
      // because useFieldArray's `fields` have an RHF-generated string `id` that shadows the numeric server `id`.
      const sections = watch('sections');
      const sectionsByKey = new Map(sections.map((s) => [s._key, s]));
      const reorderedSections = reordered
        .map((field) => sectionsByKey.get(field._key))
        .filter((s): s is TemplateSectionFormValues => s != null);
      replace(reorderedSections);
    },
    [replace, watch]
  );

  return { handleReorder };
}

export function TemplateSectionList({ fields, remove, replace, onCreateNewKpi }: TemplateSectionListProps) {
  const { handleReorder } = useTemplateSectionList(replace);
  const sortableFields = fields as SortableSectionField[];

  if (sortableFields.length === 0) {
    return null;
  }

  return (
    <div css={sectionListCss}>
      <SortableList
        items={sortableFields}
        onChange={handleReorder}
        renderItem={(field) => {
          const index = sortableFields.findIndex((f) => f.id === field.id);
          return (
            <TemplateSectionItem
              field={field}
              sectionIndex={index}
              onRemove={() => remove(index)}
              onCreateNewKpi={onCreateNewKpi}
            />
          );
        }}
      />
    </div>
  );
}
